import React from 'react';

import {Icon, WithTooltip} from '@albert-io/atomic';
import sessionStore from 'client/Session/SessionStore';
import {RestrictionIndicator} from 'client/components/ContentDiscovery/ManageRestrictionsModal';
import type {GuideTree} from 'client/components/ContentDiscovery/ManageRestrictionsModal';
import {TeacherRestrictionIndicator} from 'client/components/TeacherRestrictedContent';

interface GuideLevelHeadingIndicatorsProps {
  guideLevel: any;
  isAssessment: boolean;
  teacherRestrictedGuideLevelIds: Set<string>;
  guideLevelIdsWithLockedDescendant: Set<string>;
}

/**
 * The lock affordances that sit inline with a guide-level row's heading: the
 * teacher-restricted indicator on a restricted leaf, and the DQA #17841 roll-up icon on a
 * theme that contains locked levels.
 *
 * Each wrapper carries `role='presentation'` and swallows the click, because the row
 * itself is a `Link` (or a `role='button'` list item) that would otherwise navigate.
 */
export const GuideLevelHeadingIndicators = ({
  guideLevel,
  isAssessment,
  teacherRestrictedGuideLevelIds,
  guideLevelIdsWithLockedDescendant
}: GuideLevelHeadingIndicatorsProps) => (
  <>
    {isAssessment &&
      teacherRestrictedGuideLevelIds.has(guideLevel.getId()) &&
      !guideLevel.isTheme() && (
        <span
          role='presentation'
          className='inline-flex'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <TeacherRestrictionIndicator placement='right-start' />
        </span>
      )}
    {/* DQA #17841: themes don't carry a lock themselves, so flag a
        theme that contains locked guide levels. */}
    {isAssessment &&
      guideLevel.isTheme() &&
      guideLevelIdsWithLockedDescendant.has(guideLevel.getId()) && (
        <span
          role='presentation'
          className='inline-flex'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <WithTooltip content='Contains locked content'>
            <Icon icon='lock' iconStyle='solid' color='secondary' size='inherit' />
          </WithTooltip>
        </span>
      )}
  </>
);

interface GuideLevelRestrictionBadgeProps {
  guideLevel: any;
  isAssessment: boolean;
  guideLevelTree: GuideTree;
}

/**
 * DQA #17841: the "Restricted at N schools" badge is a larger element, so it sits below the
 * description instead of inline with the title.
 */
export const GuideLevelRestrictionBadge = ({
  guideLevel,
  isAssessment,
  guideLevelTree
}: GuideLevelRestrictionBadgeProps) => (
  <>
    {isAssessment && sessionStore.isSchoolAdministrator() && !guideLevel.isTheme() && (
      <div
        role='presentation'
        onClick={(e) => {
          // Badge sits inside the guide-level row Link: preventDefault
          // stops the native-anchor reload; stopPropagation stops the
          // row's own click handler.
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <RestrictionIndicator
          guideLevelName={guideLevel.getName()}
          guideLevelId={guideLevel.getId()}
          guideLevelTree={guideLevelTree}
          showAsBadge
          usePortal
        />
      </div>
    )}
  </>
);
